// Main React App component - Entry point for the application
// Uses React Router for section-based navigation (adapted from AgentGroupChat)

import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuthRequired } from './contexts/AuthContext';
import { LegalAcceptanceGate } from './components/LegalAcceptanceGate';
import { BetaAccessProvider } from './contexts/BetaAccessContext';
import { MainSection } from './sections/main';
import { AppSection } from './sections/app';
import { AuthSection } from './sections/auth';
import { AcceptInviteSection } from './sections/accept-invite';
import { BetaInviteSection } from './sections/beta-invite';
import { DevSection } from './sections/dev';
import { AccountSection } from './sections/account';
import { DownloadsSection as NewDownloadsSection } from './sections/downloads';
import { DownloadsAdminSection } from './sections/downloads/subpages/admin';
import { DownloadDetailsSection as NewDownloadDetailsSection } from './sections/downloads/subpages/details';
import { DownloadDetailsAdminSection } from './sections/downloads/subpages/admin/details';
import { PricingSection } from './sections/pricing';
// eslint-disable-next-line no-unused-vars -- used in Route element
import { CommunityLayout } from './sections/community/CommunityLayout';
import { CommunityBrowseSection } from './sections/community/subpages/browse';
import { CommunityDiscordSection } from './sections/community/subpages/discord';
import { AssetsSection } from './sections/assets';
import { LearnSection } from './sections/learn';
import { MarketplaceSection } from './sections/marketplace';
import { ProductsAdminSection } from './sections/marketplace/subpages/admin';
import { ProductDetailsAdminSection } from './sections/marketplace/subpages/admin/details';
import { ProductDetailsSection } from './sections/marketplace/subpages/details';
import { PurchasedProductsSection } from './sections/marketplace/subpages/purchased';
import { CheckoutSuccessSection } from './sections/checkout';
import { TutorialVideosSection } from './sections/learn/subpages/tutorial-videos';
import { QuestionsAnswersSection } from './sections/learn/subpages/questions-answers';
import { TriviaSection } from './sections/learn/subpages/trivia';
import { LearnAdminSection } from './sections/learn/subpages/admin';
import { AdminSection } from './sections/admin';
import { AdminUsersSection } from './sections/admin/subpages/users';
import { AdminStripeSection } from './sections/admin/subpages/stripe';
import { AdminInferencesSection } from './sections/admin/subpages/inferences';
import { AdminPushNotificationsSection } from './sections/admin/subpages/push-notifications';
import { AdminProductsSection } from './sections/admin/subpages/products';
import { AdminSystemInfoSection } from './sections/admin/subpages/system-info';
import { DesktopSection } from './sections/desktop';
import { DesktopModelsSection } from './sections/desktop/subpages/models';
import { DesktopDevChatSection } from './sections/desktop/subpages/chat';
import { TandemSection } from './sections/tandem';
// Global styles - import first for proper cascade order
import './styles/index.css';
import './core/utils/markdownUtils.css';
import 'katex/dist/katex.min.css';

import { AppSettingsSection } from './sections/app-settings';
import { PlayspaceSection } from './sections/playspace';
import { NotFound } from './components/NotFound';
import { UnsavedChangesModal } from './components/UnsavedChangesModal';
import { ExternalLinkModal } from './components/ExternalLinkModal';
import { DesktopUpdateToast } from './components/DesktopUpdateToast';
import { navigationManager } from './managers/navigation-manager';

// Consume notification payload from URL (from push notification click).
// Service workers cannot use sessionStorage, so payload is passed via ?np= param.
// Must run before child routes render so AdminUsersSection etc. can read from sessionStorage.
function consumeNotificationPayloadFromUrl() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const np = params.get('np');
  if (!np) return;
  try {
    const payloadData = JSON.parse(decodeURIComponent(np));
    const pathname = window.location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);
    const sectionKey = pathSegments.join('/') || 'main';
    sessionStorage.setItem(`sectionPayload_${sectionKey}`, JSON.stringify(payloadData));
    params.delete('np');
    const newSearch = params.toString();
    const newUrl = pathname + (newSearch ? '?' + newSearch : '') + (window.location.hash || '');
    window.history.replaceState({}, '', newUrl);
  } catch (e) {
    console.error('Failed to consume notification payload from URL:', e);
  }
}

// Navigation setup component - runs inside BrowserRouter context
function NavigationSetup({ children }) {
  const navigate = useNavigate();

  consumeNotificationPayloadFromUrl();

  useEffect(() => {
    // Set up navigation manager with React Router navigate function
    // This makes navigation available to all components in the app
    navigationManager.setNavigateFunction(navigate);

    // Globally disable the native context menu to keep custom menus consistent
    const handleContextMenu = (event) => {
      // Allow context menu in text inputs and textareas (for spell check, etc.)
      const target = event.target;
      if ((target.tagName === 'INPUT' && target.type === 'text') ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'A' ||
          target.contentEditable === 'true' ||
          target.closest('[data-text-input]') ||
          target.closest('[data-navigation-button]')) {
        return; // Allow default context menu
      }
      event.preventDefault();
    };
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [navigate]);

  return children;
}

// Lazy-load Workspace section so heavy dependencies (e.g. Prettier/Monaco)
// don't execute during initial app load (especially important for production builds).
const WorkspaceSectionLazy = lazy(() =>
  import('./sections/workspace').then((mod) => ({ default: mod.WorkspaceSection }))
);

function WorkspaceRoute() {
  const { appId } = useParams();

  // Require beta tester for workspace access; hook will show modal + redirect.
  const auth = useAuthRequired({
    requireAuth: true,
    requireBetaTester: false,
    payload: {
      action: 'editWorkspace',
      appId,
      timestamp: Date.now(),
    },
  });

  // While auth is loading (or if not authorized), do not import/render workspace.
  if (auth.loading || !auth.isAuthorized) return null;

  return (
    <Suspense fallback={null}>
      <WorkspaceSectionLazy />
    </Suspense>
  );
}

function AdminRoute({ children }) {
  // Require admin role for admin routes; hook will redirect if not authorized.
  const auth = useAuthRequired({
    requireAuth: true,
    requireAdmin: true,
    payload: {
      action: 'accessAdmin',
      timestamp: Date.now(),
    },
  });

  // While auth is loading (or if not authorized), do not render admin content.
  if (auth.loading || !auth.isAuthorized) return null;

  return children;
}

export function App() {
  return (
    <BrowserRouter>
      <BetaAccessProvider>
        <AuthProvider>
          <LegalAcceptanceGate>
          <NavigationSetup>
          <UnsavedChangesModal />
          <ExternalLinkModal />
          <DesktopUpdateToast />
          <div className="app">
            <Routes>
              {/* Redirect root to main section */}
              <Route path="/" element={<Navigate to="/main" replace />} />

              {/* Main landing page section */}
              <Route path="/main" element={<MainSection />} />

            {/* Auth section - Login/Register */}
            <Route path="/auth" element={<AuthSection />} />

            {/* Accept invite section (always re-auth enforced within section) */}
            <Route path="/accept-invite" element={<AcceptInviteSection />} />

            {/* Beta invite section - validate code, require auth to redeem */}
            <Route path="/beta-invite" element={<BetaInviteSection />} />

            {/* Dev section - Only available in development */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <Route path="/dev" element={<DevSection />} />
                <Route path="/dev/databases" element={<DevSection />} />
                <Route path="/dev/migration" element={<DevSection />} />
                <Route path="/dev/site-status" element={<DevSection />} />
                <Route path="/dev/status" element={<DevSection />} />
                <Route path="/dev/users" element={<DevSection />} />
                <Route path="/dev/users/:userId" element={<DevSection />} />
                <Route path="/dev/apps" element={<DevSection />} />
                <Route path="/dev/orphaned-apps" element={<DevSection />} />
                <Route path="/dev/plans" element={<DevSection />} />
                <Route path="/dev/backups" element={<DevSection />} />
                <Route path="/dev/templates" element={<DevSection />} />
                <Route path="/dev/openrouter" element={<DevSection />} />
                <Route path="/dev/model-defaults" element={<DevSection />} />
                <Route path="/dev/chat" element={<DevSection />} />
                <Route path="/dev/model-scraper" element={<DevSection />} />
                <Route path="/dev/model-tester" element={<DevSection />} />
                <Route path="/dev/chunk-analysis" element={<DevSection />} />
                <Route path="/dev/user-migration" element={<DevSection />} />
                <Route path="/dev/stripe" element={<DevSection />} />
                <Route path="/dev/products" element={<DevSection />} />
                <Route path="/dev/push" element={<DevSection />} />
                <Route path="/dev/beta-testers" element={<DevSection />} />
                <Route path="/dev/ownership-cleanup" element={<DevSection />} />
                <Route path="/dev/discord" element={<DevSection />} />
                <Route path="/dev/transfer" element={<DevSection />} />
                <Route path="/dev/gumroad" element={<DevSection />} />
              </>
            )}

            {/* App section - Projects dashboard */}
            <Route path="/app" element={<AppSection />} />

            {/* Assets section - Asset library (coming soon) */}
            <Route path="/assets" element={<AssetsSection />} />

            {/* Account section - User account management */}
            <Route path="/account" element={<AccountSection />} />

            {/* Desktop section - Desktop-specific settings (desktop app only) */}
            <Route path="/desktop/settings" element={<DesktopSection />} />
            <Route path="/desktop/models" element={<DesktopModelsSection />} />
            <Route path="/desktop/dev-chat" element={<DesktopDevChatSection />} />
            <Route path="/desktop/tandem" element={<TandemSection />} />

            {/* Downloads section - Modern downloads page */}
            <Route path="/downloads" element={<NewDownloadsSection />} />
            <Route path="/downloads/admin" element={<DownloadsAdminSection />} />
            <Route path="/downloads/details" element={<NewDownloadDetailsSection />} />
            <Route path="/downloads/admin/details" element={<DownloadDetailsAdminSection />} />


            {/* Pricing section - Subscription plans and billing */}
            <Route path="/pricing" element={<PricingSection />} />

            {/* Community section - Layout gates by supporter; non-supporters see root only */}
            <Route path="/community" element={<CommunityLayout />}>
              <Route index element={<Navigate to="/community/browse" replace />} />
              <Route path="browse" element={<CommunityBrowseSection />} />
              <Route path="discord" element={<CommunityDiscordSection />} />
              <Route path="admin/browse" element={<CommunityBrowseSection />} />
              <Route path="admin/discord" element={<CommunityDiscordSection />} />
            </Route>

            {/* Admin section - Administrative dashboard and tools */}
            <Route path="/admin" element={<AdminRoute><AdminSection /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsersSection /></AdminRoute>} />
            <Route path="/admin/stripe" element={<AdminRoute><AdminStripeSection /></AdminRoute>} />
            <Route path="/admin/inferences" element={<AdminRoute><AdminInferencesSection /></AdminRoute>} />
            <Route path="/admin/push-notifications" element={<AdminRoute><AdminPushNotificationsSection /></AdminRoute>} />
            <Route path="/admin/products" element={<AdminRoute><AdminProductsSection /></AdminRoute>} />
            <Route path="/admin/system-info" element={<AdminRoute><AdminSystemInfoSection /></AdminRoute>} />

            {/* Learn section - Learning hub for tutorials and resources */}
            <Route path="/learn" element={<LearnSection />} />
            <Route path="/learn/admin" element={<LearnAdminSection />} />
            <Route path="/learn/tutorial-videos" element={<TutorialVideosSection />} />
            <Route path="/learn/questions-answers" element={<QuestionsAnswersSection />} />
            <Route path="/learn/trivia" element={<TriviaSection />} />

            {/* Marketplace section - Products marketplace */}
            <Route path="/marketplace" element={<MarketplaceSection />} />
            <Route path="/marketplace/purchased" element={<PurchasedProductsSection />} />
            <Route path="/marketplace/admin" element={<ProductsAdminSection />} />
            <Route path="/marketplace/details" element={<ProductDetailsSection />} />
            <Route path="/marketplace/admin/details" element={<ProductDetailsAdminSection />} />

            {/* Checkout success section */}
            <Route path="/checkout/success" element={<CheckoutSuccessSection />} />
            <Route path="/checkout" element={<CheckoutSuccessSection />} />

            {/* App settings section */}
            <Route path="/app-settings/:appId" element={<AppSettingsSection />} />

            {/* Playspace section */}
            <Route path="/playspace/:appId" element={<PlayspaceSection />} />

            {/* Workspace section */}
            <Route path="/workspace/:appId" element={<WorkspaceRoute />} />

            {/* Future sections will be added here */}
            {/* <Route path="/projects" element={<ProjectSection />} /> */}
            {/* <Route path="/chat" element={<ChatSection />} /> */}

              {/* 404 Not Found - catch all unmatched routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
          </NavigationSetup>
          </LegalAcceptanceGate>
        </AuthProvider>
      </BetaAccessProvider>
    </BrowserRouter>
  );
}

// Default export for React
export default App;
